import * as THREE from 'three';
import { CONFIG } from '../config';
import { achievementDef, achievementTitle } from '../content/achievements';
import { isWoman } from '../content/looks';
import { ITEMS } from '../content/items';
import { canFullscreen, isTouch, onTouchChange, toggleFullscreen } from '../core/device';
import { formatMoney } from '../core/math';
import type { Store, Toast } from '../state/Store';
import { BUZZ_LABELS, buzzTier, dayName, formatClock, isNight, rankFor } from '../state/rules';
import type { ItemId, StatId } from '../state/types';
import { iconEl, setRich } from '../icons/dom';
import { h } from './dom';

const STAT_LABELS: Record<StatId, string> = {
  health: 'Health',
  hunger: 'Hunger',
  energy: 'Energy',
  buzz: 'Buzz',
};
const BAR_ICONS: Record<StatId, string> = {
  health: ':heart:',
  hunger: ':hotdog:',
  energy: ':bolt:',
  buzz: ':beer:',
};
/** `pad` is the game controller button for the same thing (see core/padmap.ts). */
const HOTBAR: Array<{ id: ItemId; key?: string; pad?: string; onlyWhenOwned?: boolean }> = [
  { id: 'beer', key: 'Q', pad: 'X' },
  { id: 'hotdog', key: 'F', pad: 'Y' },
  { id: 'dewd', key: 'R', pad: 'LB' },
  { id: 'scratcher', key: 'T', pad: 'RB' },
  { id: 'fireworks', key: 'G', pad: ':arrow-down:' },
  { id: 'cans' },
  { id: 'scrap' },
  { id: 'parts' },
  // Back Forty goods only take up room once you have some.
  { id: 'catfish', onlyWhenOwned: true },
  { id: 'copper', onlyWhenOwned: true },
  { id: 'shine', onlyWhenOwned: true },
  { id: 'mash', onlyWhenOwned: true },
  // Vices and fancier tickets, also only once you've got them.
  { id: 'cigs', onlyWhenOwned: true },
  { id: 'wine', onlyWhenOwned: true },
  { id: 'whiskey', onlyWhenOwned: true },
  { id: 'scratchCow', onlyWhenOwned: true },
  { id: 'scratchGator', onlyWhenOwned: true },
  { id: 'scratchMega', onlyWhenOwned: true },
  // Bigger meals, stronger stuff and your light.
  { id: 'tvDinner', onlyWhenOwned: true },
  { id: 'pizza', onlyWhenOwned: true },
  { id: 'chicken', onlyWhenOwned: true },
  { id: 'bigEnergy', onlyWhenOwned: true },
  { id: 'rocketFuel', onlyWhenOwned: true },
  { id: 'flashlight', key: 'L', onlyWhenOwned: true },
  { id: 'headlamp', key: 'L', onlyWhenOwned: true },
  { id: 'foilHat', onlyWhenOwned: true },
  { id: 'alienGizmo', onlyWhenOwned: true },
  { id: 'rascal', key: 'B', onlyWhenOwned: true },
];

interface Floater {
  el: HTMLElement;
  world: THREE.Vector3;
  age: number;
  life: number;
  rise: number;
}

/** Heads-up display: status card, clock, hotbar, prompt, ticker, toasts and world labels. */
export class Hud {
  readonly root: HTMLDivElement;
  private readonly avatar: HTMLImageElement;
  private readonly name: HTMLDivElement;
  private readonly rank: HTMLDivElement;
  private readonly money: HTMLSpanElement;
  /** The cash pill (it jumps when money comes or goes), and where the +$/-$ chips float up. */
  private readonly cash: HTMLDivElement;
  private readonly moneyFx: HTMLDivElement;
  /** The money counter rolls toward the real number instead of snapping. */
  private money0 = Number.NaN;
  private money1 = 0;
  private moneyAt = 0;
  private moneyShown = Number.NaN;
  /** The rent sticky note (paid, due soon or due today). */
  private readonly rentNote: HTMLDivElement;
  private readonly rentLine: HTMLSpanElement;
  private readonly strikes: HTMLSpanElement;
  private readonly debtLine: HTMLDivElement;
  /** What the rent and debt lines last showed (they're rebuilt only when that changes). */
  private rentShown = '';
  private debtShown = '';
  private readonly bars = new Map<
    StatId,
    {
      wrap: HTMLDivElement;
      fill: HTMLDivElement;
      /** What you just lost, trailing behind the bar for a moment. */
      ghost: HTMLDivElement;
      value: HTMLSpanElement;
      last: number;
      ghostAt: number;
      hitAt: number;
    }
  >();
  private lastFrame = performance.now();
  private readonly buzzLabel: HTMLDivElement;
  private readonly clockTime: HTMLSpanElement;
  private readonly clockAmPm: HTMLSpanElement;
  private readonly clockDay: HTMLDivElement;
  private readonly prompt: HTMLDivElement;
  private readonly promptText: HTMLSpanElement;
  private readonly promptKey: HTMLSpanElement;
  /** Hotbar key hints, with their keyboard and controller labels. */
  private readonly keyHints: Array<{ el: HTMLElement; key: string; pad: string }> = [];
  private readonly objective: HTMLDivElement;
  private readonly objectiveText: HTMLSpanElement;
  /** Riding the Lil' Rascal: battery and how to get off. */
  private readonly rideChip: HTMLDivElement;
  private padOn = false;
  private readonly objectiveArrow: HTMLSpanElement;
  private readonly slots = new Map<
    ItemId,
    { el: HTMLDivElement; count: HTMLSpanElement; optional: boolean }
  >();
  private readonly tickerTrack: HTMLDivElement;
  private readonly toasts: HTMLDivElement;
  /**
   * Where notifications pop up: just under this point (the player's feet), mid-screen when
   * there's no character to stand under (first person), or off to the side of whatever menu,
   * shop or mini-game has the screen.
   */
  toastAnchor: THREE.Vector3 | 'center' | 'side' = 'center';
  private toastX = Number.NaN;
  private toastY = Number.NaN;
  /** How many notifications fit under the character right now (worked out each frame). */
  private toastRoom = 3;
  /** The on-screen buttons on touch screens (notifications keep out of their way). */
  private touchPad: HTMLElement | null = null;
  private readonly statusEl: HTMLDivElement;
  private readonly clockEl: HTMLDivElement;
  /** The width the notification stack was last given ('' = its normal width). */
  private toastWidth = '';
  private readonly hotbar: HTMLDivElement;
  private readonly labels: HTMLDivElement;
  private readonly floaters: Floater[] = [];
  private readonly bubbles = new Map<string, { el: HTMLDivElement; until: number; world: THREE.Vector3 }>();
  private readonly tags = new Map<string, { el: HTMLDivElement; world: THREE.Vector3 | null }>();
  /** Badges over things you can use (indoors: everything nearby; outdoors: what the prompt means). */
  private readonly markers = new Map<string, { el: HTMLDivElement; world: THREE.Vector3; live: boolean }>();
  private readonly drunkFx: HTMLDivElement;
  private readonly hurtFx: HTMLDivElement;
  readonly fade: HTMLDivElement;
  private readonly fadeText: HTMLDivElement;
  private readonly fadeSub: HTMLDivElement;
  private readonly phoneBadge: HTMLSpanElement;
  private readonly musicBtn: HTMLButtonElement;
  private readonly onlineBtn: HTMLButtonElement;
  private readonly onlineBadge: HTMLSpanElement;
  private tickerItems: string[] = [];
  private lastHealth = 100;
  private achievementEl: HTMLElement | null = null;
  private readonly tmp = new THREE.Vector3();

  constructor(
    parent: HTMLElement,
    private readonly store: Store,
    handlers: {
      onPhone: () => void;
      onMusic: () => void;
      onSlot: (id: ItemId) => void;
      /** Touch devices: tapping the "E" prompt interacts. */
      onPrompt: () => void;
      onOnline: () => void;
    },
  ) {
    this.root = h('div', { class: 'hud' });
    this.root.style.position = 'absolute';
    this.root.style.inset = '0';

    this.labels = h('div', { class: 'labels' });
    this.rideChip = h('div', { class: 'ride-chip', hidden: true });
    this.drunkFx = h('div', { class: 'fx-drunk' });
    this.hurtFx = h('div', { class: 'fx-hurt' });

    this.avatar = h('img', { class: 'hud-avatar', attrs: { alt: '' } });
    this.name = h('div', { class: 'hud-name' });
    this.rank = h('div', { class: 'hud-rank' });
    this.money = h('span', { class: 'hud-money', attrs: { 'aria-live': 'polite' } });
    this.moneyFx = h('div', { class: 'money-fx', attrs: { 'aria-hidden': 'true' } });
    this.cash = h(
      'div',
      { class: 'hud-cash', title: 'Cash on hand' },
      h('span', { class: 'cash-icon', text: ':cash:' }),
      this.money,
      this.moneyFx,
    );
    this.rentLine = h('span', { class: 'rent-text' });
    this.strikes = h('span', { class: 'strikes', title: 'Rent strikes (3 = eviction)' });
    this.rentNote = h('div', { class: 'hud-rent' }, this.rentLine, this.strikes);
    this.debtLine = h('div', { class: 'hud-debt' });
    const barsEl = h('div', { class: 'bars' });
    for (const id of ['health', 'hunger', 'energy', 'buzz'] as StatId[]) {
      const fill = h('div', { class: 'bar-fill' });
      const ghost = h('div', { class: 'bar-ghost' });
      const value = h('span', { class: 'bar-value' });
      const wrap = h(
        'div',
        { class: `bar bar-${id}`, title: STAT_LABELS[id] },
        h('span', { class: 'bar-icon', text: BAR_ICONS[id] }),
        h('div', { class: 'bar-track' }, ghost, fill, value),
      );
      barsEl.append(wrap);
      this.bars.set(id, { wrap, fill, ghost, value, last: Number.NaN, ghostAt: 0, hitAt: 0 });
    }
    this.buzzLabel = h('div', { class: 'buzz-tier' });
    const status: HTMLDivElement = h(
      'div',
      { class: 'hud-status' },
      h('div', { class: 'hud-tape', attrs: { 'aria-hidden': 'true' } }),
      h(
        'div',
        { class: 'hud-top' },
        h('div', { class: 'hud-polaroid' }, this.avatar),
        h('div', { class: 'hud-who' }, this.name, this.rank, this.cash),
      ),
      this.rentNote,
      this.debtLine,
      barsEl,
      this.buzzLabel,
    );

    this.clockTime = h('span', { class: 'clock-time' });
    this.clockAmPm = h('span', { class: 'clock-ampm' });
    this.clockDay = h('div', { class: 'clock-day' });
    this.phoneBadge = h('span', { class: 'badge', hidden: true });
    this.musicBtn = h('button', {
      class: 'round-btn interactive',
      title: 'Toggle music (M)',
      attrs: { 'aria-label': 'Toggle music' },
      text: ':banjo:',
      on: { click: handlers.onMusic },
    });
    // Fullscreen is only offered on touch devices whose browser supports it (Android).
    const fullscreenBtn = h('button', {
      class: 'round-btn interactive fullscreen-btn',
      title: 'Fullscreen',
      attrs: { 'aria-label': 'Toggle fullscreen' },
      text: '⛶',
      hidden: !(isTouch() && canFullscreen()),
      on: { click: () => toggleFullscreen() },
    });
    onTouchChange((on) => (fullscreenBtn.hidden = !(on && canFullscreen())));
    // Only shown while you're in a multiplayer park.
    this.onlineBadge = h('span', { class: 'badge online-count', hidden: true });
    this.onlineBtn = h(
      'button',
      {
        class: 'round-btn interactive',
        title: 'Neighbors online (C to chat)',
        attrs: { 'aria-label': 'Neighbors online' },
        hidden: true,
        on: { click: handlers.onOnline },
      },
      ':globe:',
      this.onlineBadge,
    );
    const clock: HTMLDivElement = h(
      'div',
      { class: 'hud-clock' },
      h(
        'div',
        { class: 'clock-card' },
        h('div', { class: 'clock-face' }, this.clockTime, this.clockAmPm),
        this.clockDay,
      ),
      this.onlineBtn,
      fullscreenBtn,
      this.musicBtn,
      h(
        'button',
        {
          class: 'round-btn interactive',
          title: 'Phone (P)',
          attrs: { 'aria-label': 'Open phone' },
          on: { click: handlers.onPhone },
        },
        ':phone:',
        this.phoneBadge,
      ),
    );

    this.objectiveArrow = h('span', { class: 'arrow', text: ':arrow-up:' });
    this.objectiveText = h('span');
    this.objective = h('div', { class: 'objective', hidden: true }, this.objectiveArrow, this.objectiveText);

    this.promptText = h('span');
    this.promptKey = h('span', { class: 'keycap', text: 'E' });
    // Pointer events on the prompt are only enabled by the touch stylesheet.
    this.prompt = h(
      'div',
      { class: 'prompt', hidden: true, on: { click: handlers.onPrompt } },
      this.promptKey,
      this.promptText,
    );

    const hotbar = h('div', { class: 'hotbar interactive' });
    this.hotbar = hotbar;
    this.statusEl = status;
    this.clockEl = clock;
    for (const s of HOTBAR) {
      const count = h('span', { class: 'count' });
      const k = s.key ? h('span', { class: 'k', text: s.key }) : null;
      if (k && s.key) this.keyHints.push({ el: k, key: s.key, pad: s.pad ?? s.key });
      const el = h(
        'div',
        {
          class: 'slot',
          title: `${ITEMS[s.id].name}: ${ITEMS[s.id].desc}`,
          on: { click: () => handlers.onSlot(s.id) },
        },
        ITEMS[s.id].icon,
        k,
        count,
      );
      hotbar.append(el);
      this.slots.set(s.id, { el, count, optional: !!s.onlyWhenOwned });
    }

    this.tickerTrack = h('div', { class: 'ticker-track' });
    const ticker = h(
      'div',
      { class: 'ticker', attrs: { 'aria-hidden': 'true' } },
      h('div', { class: 'ticker-label', text: 'LOCAL 6 NEWS' }),
      this.tickerTrack,
    );

    this.toasts = h('div', { class: 'toasts', attrs: { 'aria-live': 'polite' } });

    this.fadeText = h('div', { class: 'zzz' });
    this.fadeSub = h('div', { class: 'sub' });
    this.fade = h('div', { class: 'fx-fade' }, this.fadeText, this.fadeSub);

    this.root.append(
      this.labels,
      this.drunkFx,
      this.hurtFx,
      status,
      clock,
      this.objective,
      this.rideChip,
      this.prompt,
      hotbar,
      ticker,
      this.toasts,
      this.fade,
    );
    parent.append(this.root);

    store.bus.on('toast', (t) => this.toast(t));
    store.bus.on('achievement', (id) => this.achievement(id));
    store.bus.on('headline', (text) => this.pushTicker(text, true));
    store.bus.on('item', ({ id, delta }) => {
      if (delta > 0) this.slots.get(id)?.el.classList.remove('bump');
      const slot = this.slots.get(id);
      if (slot && delta > 0) {
        void slot.el.offsetWidth;
        slot.el.classList.add('bump');
      }
    });
  }

  set visible(v: boolean) {
    this.root.style.display = v ? '' : 'none';
    // A new game or a loaded one: the counter starts where the money is, no "+$" for it.
    if (v) this.money0 = Number.NaN;
  }

  /** During an in-world mini-game only floating text, toasts and fades stay up. */
  set stageMode(on: boolean) {
    this.root.classList.toggle('stage-mode', on);
  }

  setAvatar(url: string): void {
    this.avatar.src = url;
  }

  /** Show controller buttons instead of keys (the prompt's E becomes A, and so on). */
  setPad(on: boolean): void {
    this.padOn = on;
    this.promptKey.textContent = on ? 'A' : 'E';
    for (const hint of this.keyHints) setRich(hint.el, on ? hint.pad : hint.key);
  }

  setMusic(on: boolean): void {
    this.musicBtn.style.opacity = on ? '1' : '0.45';
  }

  setUnread(n: number): void {
    this.phoneBadge.hidden = n <= 0;
    this.phoneBadge.textContent = String(n);
  }

  /** Neighbors in your park, or null when you're not online. */
  setOnline(neighbors: number | null): void {
    this.onlineBtn.hidden = neighbors === null;
    this.onlineBadge.hidden = !neighbors;
    this.onlineBadge.textContent = String(neighbors ?? 0);
  }

  update(): void {
    const s = this.store.state;
    this.name.textContent = s.player.name;
    const rating = this.store.rating();
    const { rank } = rankFor(rating, isWoman(s.player.look));
    this.rank.textContent = `${rank.title} · ${rating} pts`;
    this.updateMoney(s.money);
    const r = this.store.rentStatus();
    const when = r.daysUntil <= 0 ? 'TODAY 5PM' : r.daysUntil === 1 ? 'tomorrow' : `in ${r.daysUntil} days`;
    const rent = r.paid ? `paid ${r.week}` : `due ${r.amount} ${when}`;
    if (rent !== this.rentShown) {
      this.rentShown = rent;
      this.rentNote.classList.toggle('paid', r.paid);
      this.rentNote.classList.toggle('today', !r.paid && r.daysUntil <= 0);
      this.rentNote.classList.toggle('soon', !r.paid && r.daysUntil === 1);
      this.rentLine.replaceChildren(
        iconEl('trailer'),
        ...(r.paid
          ? [' ', h('span', { class: 'ok', text: 'Rent paid ✓' }), ` (wk ${r.week})`]
          : [' Rent ', h('span', { class: 'bad', text: `$${r.amount}` }), ` due ${when}`]),
      );
    }
    setRich(
      this.strikes,
      ':cross:'.repeat(s.rent.strikes) + ':empty:'.repeat(Math.max(0, CONFIG.maxStrikes - s.rent.strikes)),
    );
    this.strikes.classList.toggle('none', s.rent.strikes === 0);
    this.debtLine.style.display = s.debt > 0 ? '' : 'none';
    const debt = s.debt > 0 ? formatMoney(s.debt) : '';
    if (debt && debt !== this.debtShown)
      this.debtLine.replaceChildren(
        iconEl('credit-card'),
        " Owe Lucky's Payday: ",
        h('span', { class: 'bad', text: debt }),
      );
    this.debtShown = debt;
    this.updateBars(s.stats);
    const tier = buzzTier(s.stats.buzz);
    setRich(
      this.buzzLabel,
      tier === 'sober'
        ? ''
        : `:beer: ${BUZZ_LABELS[tier]}${tier === 'good' || tier === 'buzzed' ? ' (+50% Clout)' : ''}`,
    );
    const [hm = '', ampm = ''] = formatClock(s.time.minutes).split(' ');
    this.clockTime.textContent = hm;
    this.clockAmPm.textContent = ampm;
    setRich(
      this.clockDay,
      `${isNight(s.time.minutes) ? ':moon:' : ':sun:'} ${dayName(s.time.day)} · Day ${s.time.day}`,
    );
    for (const [id, slot] of this.slots) {
      const n = s.inv[id];
      slot.count.textContent = n > 0 ? String(n) : '';
      slot.el.classList.toggle('empty', n === 0);
      if (slot.optional) slot.el.hidden = n === 0;
    }
    this.drunkFx.style.opacity = String(Math.max(0, (s.stats.buzz - 55) / 60));
    if (s.stats.health < this.lastHealth - 4) this.flashHurt();
    this.lastHealth = s.stats.health;
  }

  /** Rolls the money counter toward the real number, with a +$/-$ chip when it changes. */
  private updateMoney(money: number): void {
    const now = performance.now();
    if (Number.isNaN(this.money0)) {
      this.money0 = this.money1 = this.moneyShown = money;
    } else if (Math.abs(money - this.money1) >= 0.005) {
      this.moneyChip(money - this.money1);
      this.money0 = this.moneyShown;
      this.money1 = money;
      this.moneyAt = now;
    }
    const t = Math.min(1, (now - this.moneyAt) / 650);
    this.moneyShown = this.money0 + (this.money1 - this.money0) * (1 - (1 - t) ** 3);
    const text = formatMoney(t >= 1 ? this.money1 : this.moneyShown);
    if (this.money.textContent !== text) this.money.textContent = text;
  }

  private moneyChip(delta: number): void {
    const up = delta > 0;
    const chip = h('span', {
      class: `money-chip ${up ? 'up' : 'down'}`,
      text: `${up ? '+' : '−'}${formatMoney(Math.abs(delta))}`,
    });
    this.moneyFx.append(chip);
    while (this.moneyFx.children.length > 3) this.moneyFx.firstElementChild?.remove();
    window.setTimeout(() => chip.remove(), 1500);
    this.cash.classList.remove('up', 'down');
    void this.cash.offsetWidth;
    this.cash.classList.add(up ? 'up' : 'down');
  }

  /**
   * Stat bars: the fill follows the stat, a red trail hangs on to a sudden loss for a beat
   * before it drains away, and big jumps flash the bar.
   */
  private updateBars(stats: Record<StatId, number>): void {
    const now = performance.now();
    const dt = Math.min(0.1, (now - this.lastFrame) / 1000);
    this.lastFrame = now;
    for (const [id, bar] of this.bars) {
      const v = stats[id];
      if (Number.isNaN(bar.last)) bar.last = bar.ghostAt = v;
      const jump = v - bar.last;
      if (jump <= -1.5) bar.hitAt = now;
      if (jump >= 1.5 || jump <= -1.5) {
        bar.wrap.classList.remove('gain', 'hit');
        void bar.wrap.offsetWidth;
        bar.wrap.classList.add(jump > 0 ? 'gain' : 'hit');
      }
      bar.last = v;
      // The trail waits half a second after the last hit, then drains.
      if (v >= bar.ghostAt) bar.ghostAt = v;
      else if (now - bar.hitAt > 500) bar.ghostAt = Math.max(v, bar.ghostAt - 60 * dt);
      bar.fill.style.width = `${v}%`;
      bar.ghost.style.width = `${bar.ghostAt}%`;
      const shown = String(Math.round(v));
      if (bar.value.textContent !== shown) bar.value.textContent = shown;
      bar.wrap.classList.toggle('low', id !== 'buzz' && v < 20);
    }
  }

  flashHurt(): void {
    this.hurtFx.style.opacity = '1';
    window.setTimeout(() => (this.hurtFx.style.opacity = '0'), 250);
  }

  /** While you're on the Rascal: the battery, and the buttons that matter. */
  setRide(battery: number | null): void {
    this.rideChip.hidden = battery === null;
    if (battery === null) return;
    const pct = Math.round(battery);
    const keys = this.padOn
      ? 'B hop off · LT turbo'
      : isTouch()
        ? ':hop: hop off · :croc: turbo'
        : 'Space hop off · Shift turbo · H meep';
    const text = `:rascal: ${pct <= 15 ? ':battery-low:' : ':battery:'} ${pct}% · ${keys}`;
    setRich(this.rideChip, text);
    this.rideChip.classList.toggle('low', pct <= 15);
  }

  setPrompt(text: string | null): void {
    this.prompt.hidden = !text;
    if (text) setRich(this.promptText, text);
  }

  setObjective(
    text: string | null,
    target: THREE.Vector3 | null,
    camera: THREE.Camera,
    player: THREE.Vector3,
  ): void {
    this.objective.hidden = !text;
    if (!text) return;
    setRich(this.objectiveText, text);
    this.objectiveArrow.style.display = target ? '' : 'none';
    if (target) {
      const dx = target.x - player.x;
      const dz = target.z - player.z;
      const camDir = camera.getWorldDirection(this.tmp);
      const camAngle = Math.atan2(camDir.x, camDir.z);
      const angle = Math.atan2(dx, dz) - camAngle;
      this.objectiveArrow.style.transform = `rotate(${-angle}rad)`;
      const dist = Math.hypot(dx, dz);
      setRich(this.objectiveText, `${text} · ${Math.round(dist)}m`);
    }
  }

  // --- ticker ------------------------------------------------------------------

  pushTicker(text: string, urgent = false): void {
    if (urgent) this.tickerItems.unshift(text);
    else this.tickerItems.push(text);
    if (this.tickerItems.length > 6) this.tickerItems.length = 6;
    setRich(this.tickerTrack, this.tickerItems.join('   ★   '));
    if (urgent) {
      this.tickerTrack.style.animation = 'none';
      void this.tickerTrack.offsetWidth;
      this.tickerTrack.style.animation = '';
    }
  }

  // --- toasts & popups -------------------------------------------------------------

  toast(t: Toast): void {
    const el = h(
      'div',
      { class: `toast ${t.kind ?? 'info'}` },
      t.icon ? h('span', { class: 'toast-icon', text: t.icon }) : null,
      h('span', { class: 'toast-text', text: t.text }),
    );
    // Newest right under the character. Older ones get pushed down, and when there's no
    // room left under you the oldest steps aside early.
    this.toasts.prepend(el);
    const live = [...this.toasts.children].filter((c) => !c.classList.contains('out'));
    live.slice(this.toastRoom).forEach((old) => this.dismissToast(old));
    window.setTimeout(() => this.dismissToast(el), 4200);
  }

  private dismissToast(el: Element): void {
    if (el.classList.contains('out')) return;
    el.classList.add('out');
    window.setTimeout(() => el.remove(), 420);
  }

  achievement(id: string): void {
    const def = achievementDef(id);
    if (!def) return;
    this.unlockPop(
      ':trophy: ACHIEVEMENT UNLOCKED',
      achievementTitle(def, isWoman(this.store.state.player.look)),
      `You might be a redneck if ${def.line}`,
    );
  }

  /** The big cardboard card for a trophy or a new hustle. */
  unlockPop(label: string, title: string, line: string): void {
    this.achievementEl?.remove();
    const el = h(
      'div',
      { class: 'achievement-pop cardboard' },
      h('div', { class: 'label', text: label }),
      h('div', { class: 'title', text: title }),
      h('div', { class: 'line', text: line }),
    );
    this.root.append(el);
    this.achievementEl = el;
    window.setTimeout(() => {
      el.style.transition = 'opacity 0.5s';
      el.style.opacity = '0';
      window.setTimeout(() => el.remove(), 500);
    }, 4200);
  }

  showFade(title: string, sub: string): void {
    setRich(this.fadeText, title);
    setRich(this.fadeSub, sub);
    this.fade.classList.add('on');
  }

  hideFade(): void {
    this.fade.classList.remove('on');
  }

  // --- world-anchored labels --------------------------------------------------------

  float(text: string, world: THREE.Vector3, color = '#ffd400'): void {
    const el = h('div', { class: 'float-text', text, style: { color } });
    this.labels.append(el);
    this.floaters.push({ el, world: world.clone(), age: 0, life: 1.6, rise: 1.4 });
  }

  bubble(key: string, text: string, world: THREE.Vector3, seconds = 3.5): void {
    this.bubbles.get(key)?.el.remove();
    const el = h('div', { class: 'bubble', text });
    this.labels.append(el);
    this.bubbles.set(key, { el, until: performance.now() + seconds * 1000, world: world.clone() });
  }

  moveBubble(key: string, world: THREE.Vector3): void {
    const b = this.bubbles.get(key);
    if (b) b.world.copy(world);
  }

  /** A name tag pinned over someone's head. Pass `world: null` to hide it for now. */
  setTag(key: string, text: string, world: THREE.Vector3 | null, cls = ''): void {
    let tag = this.tags.get(key);
    if (!tag) {
      tag = { el: h('div', { class: `name-tag ${cls}`.trim() }), world: null };
      // Tags go underneath speech bubbles and floating text.
      this.labels.prepend(tag.el);
      this.tags.set(key, tag);
    }
    setRich(tag.el, text);
    tag.world = world ? (tag.world ?? new THREE.Vector3()).copy(world) : null;
  }

  removeTag(key: string): void {
    this.tags.get(key)?.el.remove();
    this.tags.delete(key);
  }

  /** Shows exactly these markers; `current` is the one E would use. */
  setMarkers(
    list: ReadonlyArray<{
      id: string;
      icon: string;
      x: number;
      y: number;
      z: number;
      current: boolean;
      far: number;
    }>,
  ): void {
    for (const m of this.markers.values()) m.live = false;
    for (const it of list) {
      let m = this.markers.get(it.id);
      if (!m) {
        // On touch screens the current one gets a little finger ("tap the prompt") instead of an E.
        const el = h(
          'div',
          { class: 'marker' },
          h('span', { class: 'marker-icon', text: it.icon }),
          h('span', { class: 'marker-tap', text: ':point-up:' }),
        );
        this.labels.prepend(el);
        m = { el, world: new THREE.Vector3(), live: true };
        this.markers.set(it.id, m);
      }
      m.live = true;
      m.world.set(it.x, it.y, it.z);
      m.el.classList.toggle('current', it.current);
      m.el.style.opacity = it.current ? '1' : String(Math.max(0.35, 1 - it.far));
    }
    for (const [id, m] of this.markers)
      if (!m.live) {
        m.el.remove();
        this.markers.delete(id);
      }
  }

  updateLabels(dt: number, camera: THREE.Camera, width: number, height: number): void {
    this.placeToasts(dt, camera, width, height);
    for (const m of this.markers.values()) this.place(m.el, m.world, camera, width, height);
    for (let i = this.floaters.length - 1; i >= 0; i--) {
      const f = this.floaters[i] as Floater;
      f.age += dt;
      const p = f.age / f.life;
      this.tmp.copy(f.world);
      this.tmp.y += f.rise * p;
      this.place(f.el, this.tmp, camera, width, height);
      f.el.style.opacity = String(1 - Math.max(0, p - 0.6) / 0.4);
      if (p >= 1) {
        f.el.remove();
        this.floaters.splice(i, 1);
      }
    }
    for (const tag of this.tags.values()) {
      if (tag.world) this.place(tag.el, tag.world, camera, width, height);
      else tag.el.style.display = 'none';
    }
    const now = performance.now();
    for (const [key, b] of this.bubbles) {
      if (now > b.until) {
        b.el.remove();
        this.bubbles.delete(key);
        continue;
      }
      this.place(b.el, b.world, camera, width, height);
    }
  }

  /**
   * Notifications sit right below the character (where you're already looking), clear of
   * the prompt, the hotbar and the controls at the bottom, and glide as the camera moves.
   */
  private placeToasts(dt: number, camera: THREE.Camera, width: number, height: number): void {
    const box = this.toasts;
    if (!box.firstElementChild) {
      // Next one appears right where it belongs instead of gliding over from the last spot.
      this.toastX = this.toastY = Number.NaN;
      return;
    }
    if (this.toastAnchor === 'side') {
      this.placeToastsAside(width);
      return;
    }
    this.sizeToasts('', '');
    const w = box.offsetWidth;
    const hgt = box.offsetHeight;
    let x = width / 2;
    let y = height * 0.6;
    if (this.toastAnchor instanceof THREE.Vector3) {
      const v = this.tmp.copy(this.toastAnchor).project(camera);
      if (v.z > -1 && v.z < 1 && Math.abs(v.x) < 1 && Math.abs(v.y) < 1) {
        x = (v.x * 0.5 + 0.5) * width;
        y = (-v.y * 0.5 + 0.5) * height + 16;
      }
    }
    // The lowest it can go: above whatever's along the bottom of the screen.
    let floor = height - 12;
    for (const el of [this.prompt, this.rideChip, this.objective, this.hotbar]) {
      if (el.hidden || !el.offsetParent) continue;
      const top = el.getBoundingClientRect().top;
      if (top > height * 0.45) floor = Math.min(floor, top - 10);
    }
    const one = (box.firstElementChild as HTMLElement).offsetHeight + 7;
    this.toastRoom = Math.max(1, Math.min(3, Math.floor((floor - y + 7) / one)));
    y = Math.max(70, Math.min(y, floor - hgt));
    x = Math.min(Math.max(x, w / 2 + 8), width - w / 2 - 8);
    // On touch screens, slide over rather than cover the buttons.
    this.touchPad ??= document.querySelector<HTMLElement>('.touch-buttons');
    if (this.touchPad?.offsetParent) {
      const pad = this.touchPad.getBoundingClientRect();
      if (y + hgt > pad.top && x + w / 2 > pad.left - 8) x = Math.max(w / 2 + 8, pad.left - 8 - w / 2);
    }
    const k = Number.isNaN(this.toastX) ? 1 : Math.min(1, dt * 9);
    this.toastX = Number.isNaN(this.toastX) ? x : this.toastX + (x - this.toastX) * k;
    this.toastY = Number.isNaN(this.toastY) ? y : this.toastY + (y - this.toastY) * k;
    box.style.transform = `translate(${Math.round(this.toastX - w / 2)}px, ${Math.round(this.toastY)}px)`;
  }

  /** Gives the notification stack a width and a side to line up on (only touching the DOM on change). */
  private sizeToasts(width: string, side: string): void {
    if (this.toastWidth !== width) {
      this.toastWidth = width;
      this.toasts.style.width = width;
    }
    if ((this.toasts.dataset.side ?? '') !== side) this.toasts.dataset.side = side;
  }

  /**
   * A menu, shop or mini-game has the screen: notifications go beside it (the roomier side),
   * or across the top when there's no room beside it (phones).
   */
  private placeToastsAside(width: number): void {
    const box = this.toasts;
    let panel: DOMRect | null = null;
    for (const d of document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]')) {
      if (!d.offsetParent) continue;
      const r = d.getBoundingClientRect();
      if (r.width > 0 && (!panel || r.width * r.height > panel.width * panel.height)) panel = r;
    }
    const right = panel ? width - panel.right - 24 : width;
    const left = panel ? panel.left - 24 : 0;
    let x: number;
    let y: number;
    if (right >= 220) {
      const w = Math.min(360, right);
      this.sizeToasts(`${w}px`, 'right');
      x = width - 12 - w;
      y = this.clockEl.offsetParent ? this.clockEl.getBoundingClientRect().bottom + 12 : 70;
    } else if (left >= 220) {
      const w = Math.min(360, left);
      this.sizeToasts(`${w}px`, 'left');
      x = 12;
      y = this.statusEl.offsetParent ? this.statusEl.getBoundingClientRect().bottom + 12 : 70;
    } else {
      this.sizeToasts('', '');
      x = (width - box.offsetWidth) / 2;
      y = 8;
    }
    this.toastX = x + box.offsetWidth / 2;
    this.toastY = y;
    box.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
  }

  private place(
    el: HTMLElement,
    world: THREE.Vector3,
    camera: THREE.Camera,
    width: number,
    height: number,
  ): void {
    const v = this.tmp.copy(world).project(camera);
    if (v.z > 1 || v.z < -1) {
      el.style.display = 'none';
      return;
    }
    el.style.display = '';
    el.style.left = `${(v.x * 0.5 + 0.5) * width}px`;
    el.style.top = `${(-v.y * 0.5 + 0.5) * height}px`;
  }
}
